/**
 * TOSS Web Application — Client Portal realtime
 *
 * Live updates for the two portal surfaces that have them: the company chat and the
 * notification feed.
 *
 * **Hub variant (а)** (contract §6/§8): there are no client-specific hubs. The portal connects
 * to the SAME `/hubs/messenger` and `/hubs/notifications` the internal system uses, once
 * `HubAccessAuthorizationHandler` admits `ApiAudience.External` for an `Approved` account.
 * `MessengerHub.OnConnectedAsync` then subscribes an external connection to its own company's
 * chat groups and nothing else, and `JoinChat` is membership-checked, so a client connection
 * can only ever hear its own chat.
 *
 * Two rules this module exists to enforce:
 *
 *  1. **Realtime is an optimisation, never a dependency.** If the hub refuses the connection —
 *     which it does until the backend relaxation ships, and may on a network that blocks
 *     WebSockets — nothing here throws and nothing here blocks: `useClientChatMessages` falls
 *     back to polling on the `isRealtimeConnected` flag this hook returns.
 *  2. **A hub payload is never written into the portal's cache.** `ReceiveMessage` carries the
 *     INTERNAL `ChatMessageDto`: it has `fileUrl` (the anonymous `/api/files/{id}` route,
 *     ADR-022), `senderPhotoUrl`, `mentionedUserIds` and no `senderSide`. Splicing that object
 *     into `clientKeys.chat.messages()` would put internal-shaped rows — and an anonymous file
 *     URL — into a customer's screen. So an event only ever INVALIDATES; the refetch comes back
 *     through `/api/client/*` with the client projection.
 *
 * `features/messenger/hooks/messengerHubManager.ts` is off-limits (ESLint boundary, decision
 * Р6), so the portal owns its own `SignalRManager` for the messenger hub. It is created lazily
 * at module scope, exactly like the internal one, so React StrictMode's double mount and any
 * future second consumer share a single socket.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query';
import { SignalRManager, signalRManager } from '@/realtime';
import { clientKeys } from '@/hooks/api/clientPortal/clientQueryKeys';
import { removeChatFromCachedLists } from '@/hooks/api/clientPortal/clientChatCache';
import { useAuthStore } from '@/store/authStore';
import { SIGNALR_HUBS, UserRole, type SignalRConnectionState } from '@/types';
import { MessengerHubMethods } from '@/types/messenger.types';
import type {
  ChatDeletedEvent,
  FileProcessedEvent,
  MessageReactionsChangedEvent,
  StopTypingEvent,
  UserTypingEvent,
} from '@/types/messenger.types';
import type { ClientMessageListResponse } from '@/api/clientPortal/clientPortal.types';
import { NotificationHubMethods } from '@/types/notification.types';

// ============================================================================
// Hub instance
// ============================================================================

let clientMessengerHub: SignalRManager | null = null;

/**
 * The portal's messenger hub connection.
 *
 * Lazy and module-scoped: one socket per tab, whatever mounts first.
 */
function getClientMessengerHub(): SignalRManager {
  if (!clientMessengerHub) {
    clientMessengerHub = new SignalRManager({
      url: SIGNALR_HUBS.MESSENGER,
      autoReconnect: true,
    });
  }
  return clientMessengerHub;
}

/**
 * How many mounted conversations are using the socket, and the stop waiting to see whether one
 * more is about to mount.
 *
 * The socket used to be stopped by the unmounting conversation's cleanup, generation-guarded. That
 * guard does not cover an unmount and a mount in the SAME commit — switching chats (the
 * conversation is keyed by chat id), or StrictMode's double effect in development: the new mount's
 * `startConnection()` found the old connection still connected or connecting and returned without
 * a new one, then the old cleanup's stop landed, nulled the connection and cleared every handler.
 * The cabinet dropped to 15-second polling and heard no event at all — a chat deleted by a
 * colleague stayed on screen (stand, 30.09.2026: every connection closed by the client within the
 * second it opened). Counting users and deferring the stop lets the next mount keep the socket.
 *
 * The delay is seconds, not one tick: on a computer, deleting or leaving a chat unmounts the
 * conversation, and the page opens the next one from an effect a render later — a zero timer can
 * fire in between and stop the socket under the conversation that is just mounting.
 */
const CLIENT_HUB_RELEASE_MS = 3_000;
let clientMessengerHubUsers = 0;
let clientMessengerHubStop: ReturnType<typeof setTimeout> | null = null;

/**
 * Force the portal's messenger hub to re-announce itself to the server (client-portal Р4,
 * decision Р20 — the company switcher).
 *
 * `MessengerHub.OnConnectedAsync` resolves an external caller's chat groups ONCE, at
 * connect time, from the company `Users.CustomerId` names then; SignalR groups are sticky
 * and nothing re-evaluates them on their own. After a company switch a tab that keeps its
 * old socket would go on receiving the PREVIOUS company's chat for the rest of the
 * connection's life. `SignalRManager.reconnect()` is the tool for this specifically
 * because it does NOT clear the manager's subscription registry the way
 * {@link SignalRManager.stopConnection} does — {@link useClientChatRealtime}'s own
 * `ReceiveMessage` / `MessageEdited` / `MessageDeleted` handlers stay registered on the
 * rebuilt connection with no caller having to re-subscribe them.
 *
 * A no-op when the hub was never connected this session (nobody has opened
 * `/client/messenger` yet) — the next mount of {@link useClientChatRealtime} connects
 * fresh under whatever company is active by then.
 */
export async function restartClientMessengerHub(): Promise<void> {
  await getClientMessengerHub().reconnect();
}

// ============================================================================
// Constants
// ============================================================================

/**
 * How long events are coalesced before one refetch.
 *
 * A burst — a dispatcher pasting three files, or a card posted right after a message — would
 * otherwise refetch every loaded page of the infinite query once per event.
 */
const INVALIDATE_DEBOUNCE_MS = 400;

/**
 * How long a «печатает…» line survives without a refresh from its sender.
 *
 * The hub's own cleanup service expires typing state after five seconds, and a socket that drops
 * mid-sentence sends no `StopTyping` at all — without this the line would hang forever.
 */
const TYPING_EXPIRY_MS = 5_000;

/**
 * How often the composer is allowed to announce typing while somebody keeps writing, and how
 * long after the last keystroke the announcement is withdrawn.
 */
const TYPING_THROTTLE_MS = 1_500;

// ============================================================================
// Shared helpers
// ============================================================================

/** Whether realtime may run: an authenticated session whose role is `Client`. */
function useIsClientSession(): boolean {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isClient = useAuthStore((state) => state.user?.role === UserRole.Client);
  return isAuthenticated && isClient;
}

/**
 * A debounced "refetch these keys" callback, cancelled on unmount.
 */
function useDebouncedInvalidate(
  queryClient: QueryClient,
  invalidate: (client: QueryClient) => void
): () => void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const invalidateRef = useRef(invalidate);

  // Latest-ref, updated after render rather than during it: the callers pass an inline arrow,
  // so its identity changes every render while its behaviour never does.
  useEffect(() => {
    invalidateRef.current = invalidate;
  });

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    []
  );

  return useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      invalidateRef.current(queryClient);
    }, INVALIDATE_DEBOUNCE_MS);
  }, [queryClient]);
}

// ============================================================================
// Chat realtime
// ============================================================================

export interface UseClientChatRealtimeOptions {
  /**
   * Id of the company chat, from `GET /api/client/messenger/chat`. Until it is known the hook
   * still connects (so the connection is warm) but cannot join the chat group explicitly.
   */
  chatId?: string | null;
  /** Turn the whole thing off (e.g. while the chat query is still failing). Default `true`. */
  enabled?: boolean;
  /**
   * The chat named by `chatId` has just been deleted — «for everyone» by anybody, or «for me» by
   * this same account on another tab or device. The list is refetched either way; this is for the
   * screen that has the conversation OPEN, which otherwise stays on a card that now answers 404
   * («for everyone») or on history that was just cleared («for me»).
   */
  onChatDeleted?: () => void;
}

export interface UseClientChatRealtimeResult {
  /** Feed this into `useClientChatMessages` — `false` switches that query to polling. */
  isConnected: boolean;
  connectionState: SignalRConnectionState;
  /**
   * «Иван Петров печатает…», or `null` when nobody is.
   *
   * The NAME rather than the side, unlike the card signature: whoever is typing may be a
   * colleague of the customer's own company as easily as a dispatcher, and «ТОСС печатает…»
   * would be a lie half the time. The name is already visible above every bubble they send
   * (the Р17 carve-out), so this shows nothing new.
   */
  typingLabel: string | null;
  /** Call on every keystroke; throttled internally and withdrawn automatically. */
  notifyTyping: () => void;
  /** Call when the message is sent or the composer loses focus. */
  stopTyping: () => void;
}

/**
 * Keep the company chat live.
 *
 * @example
 * ```tsx
 * const { data: chat } = useClientChat();
 * const { isConnected } = useClientChatRealtime({ chatId: chat?.id });
 * const messages = useClientChatMessages({ isRealtimeConnected: isConnected });
 * ```
 */
export function useClientChatRealtime(
  options?: UseClientChatRealtimeOptions
): UseClientChatRealtimeResult {
  const { chatId, enabled = true, onChatDeleted } = options ?? {};
  const queryClient = useQueryClient();
  const isClientSession = useIsClientSession();
  const isEnabled = enabled && isClientSession;

  // Latest-ref, as in `useDebouncedInvalidate`: the subscription below is made once per connection,
  // and must still compare against the chat open NOW and call the screen's current callback.
  const deletedTargetRef = useRef({ chatId, onChatDeleted });
  useEffect(() => {
    deletedTargetRef.current = { chatId, onChatDeleted };
  });

  const hub = getClientMessengerHub();
  const [connectionState, setConnectionState] = useState<SignalRConnectionState>(() => hub.state);

  const refetchChat = useDebouncedInvalidate(queryClient, (client) => {
    // The list FIRST, and outside the `chatId` guard. An event can be about a chat this cabinet
    // has never heard of — the dispatcher's side has just created one, and its first line («Чат
    // создан») is the only announcement the cabinet gets, since `ChatCreated` carries the internal
    // shape and is deliberately withheld from an external audience. Behind the guard this call did
    // nothing in exactly the case that needed it: a new chat while none is open.
    void client.invalidateQueries({ queryKey: clientKeys.chat.lists() });
    if (!chatId) return;
    void client.invalidateQueries({ queryKey: clientKeys.chat.messages(chatId) });
    void client.invalidateQueries({ queryKey: clientKeys.chat.card(chatId) });
  });

  /**
   * The chat's CARD alone — for events that change no message.
   *
   * `MessageRead` moves `tossLastReadAt` (the customer's own ticks blacken), and the two unread
   * events move `unreadCount` (the bottom-nav badge). Refetching the whole paged history for
   * either would re-render the conversation under the reader's thumb for nothing.
   *
   * This is the reason the card sits one segment BELOW `detail(chatId)` in the key tree:
   * `invalidateQueries` matches by prefix, so a card living directly on `detail(chatId)` would
   * drag every loaded message page in with it and undo exactly what this function is for.
   */
  const refetchChatMeta = useDebouncedInvalidate(queryClient, (client) => {
    // The list is unconditional here for the same reason as in {@link refetchChat}: a chat
    // appearing, being renamed or losing a member is news for the list whether or not anything is
    // open on screen.
    void client.invalidateQueries({ queryKey: clientKeys.chat.lists() });
    if (!chatId) return;
    void client.invalidateQueries({ queryKey: clientKeys.chat.card(chatId) });
  });

  const [typingNames, setTypingNames] = useState<string[]>([]);
  const typingTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const outgoingTypingRef = useRef<{ sentAt: number; idle: ReturnType<typeof setTimeout> | null }>({
    sentAt: 0,
    idle: null,
  });

  // Connection state → the polling flag, and a catch-up refetch after every reconnect (events
  // that happened while the socket was down are simply gone).
  useEffect(() => {
    const unsubscribe = hub.onStateChange((state) => {
      setConnectionState(state);
      if (state === 'Connected') {
        refetchChat();
      }
    });
    return unsubscribe;
  }, [hub, refetchChat]);

  // Connect on the first user, disconnect a moment after the last one leaves — see
  // `clientMessengerHubUsers`. A rejected connection is swallowed on purpose: the message query polls.
  useEffect(() => {
    if (!isEnabled) return;

    clientMessengerHubUsers += 1;
    if (clientMessengerHubStop) {
      clearTimeout(clientMessengerHubStop);
      clientMessengerHubStop = null;
    }
    hub.startConnection().catch(() => {
      /* Hub unavailable — polling keeps the chat usable. */
    });

    return () => {
      clientMessengerHubUsers -= 1;
      if (clientMessengerHubUsers > 0) return;
      clientMessengerHubStop = setTimeout(() => {
        clientMessengerHubStop = null;
        if (clientMessengerHubUsers === 0) void hub.stopConnection();
      }, CLIENT_HUB_RELEASE_MS);
    };
  }, [isEnabled, hub]);

  // Join the chat group explicitly. `OnConnectedAsync` already joins every chat this account is
  // a member of, but the company chat is created lazily on first `GET /messenger/chat` — a
  // connection opened before that first call has no group to be in. The call is
  // membership-checked server-side, so a redundant one costs nothing and an illegitimate one
  // does nothing.
  useEffect(() => {
    if (!isEnabled || !chatId || connectionState !== 'Connected') return;
    hub.invoke('JoinChat', chatId).catch(() => {
      /* Not fatal: OnConnectedAsync covers the normal case. */
    });
  }, [isEnabled, chatId, connectionState, hub]);

  // Events. Subscriptions survive a rebuilt connection (the manager replays its registry), so
  // they are registered once rather than gated on the connected state.
  //
  // Edits and deletions matter as much as new messages here: with realtime connected the query
  // does not poll, so a message a dispatcher deleted would otherwise stay on the customer's
  // screen until they navigated away.
  useEffect(() => {
    if (!isEnabled) return;

    const timers = typingTimersRef.current;

    const forgetTypist = (userId: string): void => {
      const timer = timers.get(userId);
      if (timer) clearTimeout(timer);
      timers.delete(userId);
    };

    const onTyping = (event: UserTypingEvent): void => {
      if (!event?.userId || !event.userName) return;

      forgetTypist(event.userId);
      timers.set(
        event.userId,
        setTimeout(() => {
          timers.delete(event.userId);
          setTypingNames((names) => names.filter((name) => name !== event.userName));
        }, TYPING_EXPIRY_MS)
      );

      setTypingNames((names) => (names.includes(event.userName) ? names : [...names, event.userName]));
    };

    const onStoppedTyping = (event: StopTypingEvent): void => {
      if (!event?.userId) return;
      forgetTypist(event.userId);

      // The stop event carries no name, so the line is cleared wholesale rather than by name.
      // With one counterparty typing at a time — which is the normal case in a chat between a
      // company and its dispatcher — the two are the same thing.
      setTypingNames([]);
    };

    // Реакция пришла от другого участника. Правится ровно одно сообщение в кэше, без обращения к
    // серверу: событие несёт полный набор. `reactedByMe` в рассылке всегда false — своё читатель
    // знает по ответу собственной мутации, поэтому здесь оно восстанавливается по именам… которых
    // достаточно: в кабинете тёзок среди участников одной переписки не бывает, а ошибиться в свою
    // пользу нельзя — пилюля просто не подсветится до следующей загрузки страницы.
    const onReactions = (event: MessageReactionsChangedEvent): void => {
      if (!event?.chatId || !event.messageId) return;

      queryClient.setQueryData<InfiniteData<ClientMessageListResponse>>(
        clientKeys.chat.messages(event.chatId),
        (current) => {
          if (!current) return current;
          return {
            ...current,
            pages: current.pages.map((page) => {
              const items = page.items ?? [];
              if (!items.some((item) => item.id === event.messageId)) return page;
              return {
                ...page,
                items: items.map((item) =>
                  item.id === event.messageId
                    ? {
                        ...item,
                        reactions: (event.reactions ?? []).map((reaction) => ({
                          ...reaction,
                          reactedByMe:
                            item.reactions?.find((mine) => mine.emoji === reaction.emoji)
                              ?.reactedByMe ?? false,
                        })),
                      }
                    : item
                ),
              };
            }),
          };
        }
      );
    };

    // A message ends whatever typing was being announced: the sentence arrived.
    const onMessage = (): void => {
      setTypingNames([]);
      refetchChat();
    };

    // Of the internal-shaped payload only the file id is read, and only to invalidate: the fresh
    // signed URL, poster and status come back through `/api/client/files/{id}/playback`.
    const onFileProcessed = (event: FileProcessedEvent): void => {
      refetchChat();
      if (!event?.fileId) return;
      void queryClient.invalidateQueries({ queryKey: clientKeys.files.playback(event.fileId) });
    };

    const unsubscribers = [
      hub.on(MessengerHubMethods.ReceiveMessage, onMessage),
      hub.on(MessengerHubMethods.MessageEdited, refetchChat),
      hub.on(MessengerHubMethods.MessageDeleted, refetchChat),

      // Video release: a video's own row changes (fileName/contentType/size normalised, or the
      // 720p copy landed) once compression finishes, AFTER the message that carries it was
      // already sent. Same bare-signal treatment as `MessageEdited` — the payload carries the
      // internal shape (rule at the top of this file) and is not read; `refetchChat` re-reads the
      // conversation through `/api/client/*`, which is what actually picks up the new
      // `fileContentType`/`fileName`/`sizeBytes`. The tile's own `useClientFilesPlayback` polls
      // `/playback` while `processingStatus` is `Pending`/`Processing`, but its answer outranks the
      // message's, so without a nudge the tile stayed «обрабатывается» for up to one poll interval
      // after the server said Ready — `onFileProcessed` invalidates that one lookup too.
      hub.on(MessengerHubMethods.FileProcessed, onFileProcessed),
      hub.on(MessengerHubMethods.MessageReactionsChanged, onReactions),
      hub.on(MessengerHubMethods.UserTyping, onTyping),
      hub.on(MessengerHubMethods.UserStoppedTyping, onStoppedTyping),

      // The ТОСС side opened the chat: the customer's own ticks become double.
      hub.on(MessengerHubMethods.MessageRead, refetchChatMeta),

      // The «Сообщения» badge, live — it used to wait up to a minute for its own poll.
      hub.on(MessengerHubMethods.ChatUnreadIncremented, refetchChatMeta),
      hub.on(MessengerHubMethods.UnreadCountChanged, refetchChatMeta),

      // The chat ITSELF changed: renamed, deleted, somebody joined or left. All four used to be
      // heard by nobody on this side, so a chat the dispatcher deleted stayed in the customer's
      // list until the badge's own poll came round a minute later.
      //
      // Every one of them is a bare signal here. `ChatUpdated` carries `ChatShortDto` and
      // `MemberAdded` a `ChatMemberDto`, both internal shapes — the payload is not read, not
      // spliced and not rendered; it only says «ask again through /api/client/*».
      hub.on(MessengerHubMethods.ChatUpdated, refetchChatMeta),
      // `chatId` is the one field of this payload that is read: as a key to cut the row out of the
      // cached lists at once (the refetch below is too late for the page's own auto-open, see
      // `removeChatFromCachedLists`), and to compare — is the chat that went away the one on screen.
      hub.on(MessengerHubMethods.ChatDeleted, (event: ChatDeletedEvent) => {
        if (event?.chatId) removeChatFromCachedLists(queryClient, event.chatId);
        refetchChatMeta();
        const target = deletedTargetRef.current;
        if (target.chatId && event?.chatId === target.chatId) target.onChatDeleted?.();
      }),
      hub.on(MessengerHubMethods.MemberAdded, refetchChatMeta),
      hub.on(MessengerHubMethods.MemberRemoved, refetchChatMeta),
    ];

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, [isEnabled, hub, queryClient, refetchChat, refetchChatMeta]);

  // Outgoing typing. Throttled to one announcement per TYPING_THROTTLE_MS while somebody keeps
  // writing, and withdrawn the same interval after the last keystroke — otherwise a customer who
  // starts a sentence and puts the phone down leaves «печатает…» on a dispatcher's screen.
  const stopTyping = useCallback(() => {
    const outgoing = outgoingTypingRef.current;
    if (outgoing.idle) {
      clearTimeout(outgoing.idle);
      outgoing.idle = null;
    }
    if (!outgoing.sentAt) return;

    outgoing.sentAt = 0;
    if (chatId) {
      hub.invoke('StopTyping', chatId).catch(() => {
        /* Best effort: the server expires typing state on its own after five seconds. */
      });
    }
  }, [chatId, hub]);

  const notifyTyping = useCallback(() => {
    if (!isEnabled || !chatId || connectionState !== 'Connected') return;

    const outgoing = outgoingTypingRef.current;
    const now = Date.now();

    if (now - outgoing.sentAt > TYPING_THROTTLE_MS) {
      outgoing.sentAt = now;
      hub.invoke('SendTyping', chatId).catch(() => {
        /* Best effort — a missing typing indicator is not worth an error. */
      });
    }

    if (outgoing.idle) clearTimeout(outgoing.idle);
    outgoing.idle = setTimeout(stopTyping, TYPING_THROTTLE_MS);
  }, [isEnabled, chatId, connectionState, hub, stopTyping]);

  useEffect(
    () => () => {
      const outgoing = outgoingTypingRef.current;
      if (outgoing.idle) {
        clearTimeout(outgoing.idle);
        outgoing.idle = null;
      }
    },
    []
  );

  return {
    isConnected: connectionState === 'Connected',
    connectionState,
    typingLabel:
      typingNames.length === 0
        ? null
        : typingNames.length === 1
          ? `${typingNames[0]} печатает…`
          : `${typingNames.join(', ')} печатают…`,
    notifyTyping,
    stopTyping,
  };
}

// ============================================================================
// Notification feed realtime
// ============================================================================

export interface UseClientNotificationsRealtimeResult {
  isConnected: boolean;
  connectionState: SignalRConnectionState;
}

/**
 * Refresh the notification feed and its badge when the server pushes one.
 *
 * Reuses the DEFAULT `signalRManager` — the `/hubs/notifications` singleton that
 * `app/providers.tsx` already starts for every authenticated session, clients included. This
 * hook therefore only subscribes; it never calls `startConnection()` and never stops the
 * connection, which belongs to the provider.
 *
 * The event payload (the internal `Notification` DTO, wrapped in `{ eventType, notification }`)
 * is deliberately ignored: it carries `userId`, `taskId` and `expenseId`, and its `type` has not
 * been collapsed into the portal's three buckets (review decision Р5). Invalidate, refetch,
 * render what `/api/client/notifications` says.
 *
 * There is NO push in Р1 (decision Р12, rev. 2026-08-18) — this is a live in-app feed only.
 */
export function useClientNotificationsRealtime(): UseClientNotificationsRealtimeResult {
  const queryClient = useQueryClient();
  const isClientSession = useIsClientSession();
  const [connectionState, setConnectionState] = useState<SignalRConnectionState>(
    () => signalRManager.state
  );

  const refetchNotifications = useDebouncedInvalidate(queryClient, (client) => {
    // Covers both the paged list and the unread counter the bell / bottom nav read.
    void client.invalidateQueries({ queryKey: clientKeys.notifications.all });
  });

  useEffect(() => {
    const unsubscribe = signalRManager.onStateChange((state) => {
      setConnectionState(state);
      if (state === 'Connected') {
        refetchNotifications();
      }
    });
    return unsubscribe;
  }, [refetchNotifications]);

  useEffect(() => {
    if (!isClientSession) return;
    return signalRManager.on(NotificationHubMethods.ReceiveNotification, refetchNotifications);
  }, [isClientSession, refetchNotifications]);

  return {
    isConnected: connectionState === 'Connected',
    connectionState,
  };
}
